/**
 * Public dynamic calculator page — drives every /tools/{slug} URL.
 *
 * Fetches the calculator config + faq + related links from the engine,
 * renders the form and result via the reusable components, and emits
 * SEO + structured-data tags in the document head.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, BookOpen, Wrench } from "lucide-react";
import { toast } from "sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import CalculatorForm from "@/components/calculators/CalculatorForm";
import CalculatorResult from "@/components/calculators/CalculatorResult";
import CalculatorFAQ from "@/components/calculators/CalculatorFAQ";
import SoftPaywallGate from "@/components/tools/SoftPaywallGate";
import useCalcSoftPaywall from "@/components/tools/useCalcSoftPaywall";
import { useToolSeo } from "@/components/tools/seo";
import {
    fetchCalculator, runCalculator, fetchCalculatorCatalog,
} from "@/lib/calculatorApi";
import { categoryLabel } from "@/lib/tools/category-meta";


const FRONTEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function CalculatorPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [calc, setCalc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [submittedInputs, setSubmittedInputs] = useState(null);
    const [result, setResult] = useState(null);
    const { gated, tryShowResult, unlock } = useCalcSoftPaywall();
    const [relatedCalcs, setRelatedCalcs] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Reset state in the same task that begins the fetch so we don't
            // get a flash of the previous calculator's result while loading.
            if (cancelled) return;
            setLoading(true);
            setResult(null);
            setSubmittedInputs(null);
            try {
                const data = await fetchCalculator(slug);
                if (cancelled) return;
                if (!data) {
                    navigate("/tools", { replace: true });
                    return;
                }
                setCalc(data);
                // Best-effort prefetch of related calculator metadata.
                if (data.related_calculator_slugs?.length) {
                    try {
                        const all = await fetchCalculatorCatalog();
                        if (cancelled) return;
                        const map = new Map((all.items || []).map((c) => [c.slug, c]));
                        setRelatedCalcs(
                            data.related_calculator_slugs
                                .map((s) => map.get(s))
                                .filter(Boolean),
                        );
                    } catch (_) { /* ignore */ }
                }
            } catch (e) {
                navigate("/tools", { replace: true });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug, navigate]);

    const handleSubmit = async (values) => {
        setBusy(true);
        try {
            const r = await runCalculator(slug, values);
            setSubmittedInputs(values);
            if (!tryShowResult(r, setResult)) {
                setBusy(false);
                return;
            }
            // Scroll the result into view on mobile.
            setTimeout(() => {
                document.getElementById("calc-result")?.scrollIntoView({
                    behavior: "smooth", block: "start",
                });
            }, 100);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Calculation failed.");
        } finally {
            setBusy(false);
        }
    };

    const canonical = `${FRONTEND_BASE}/tools/${calc?.slug || slug}`;
    // Absolute URL for Open Graph image (must be absolute for social previews).
    const ogImageUrl = calc?.pinterest?.image_url
        ? (calc.pinterest.image_url.startsWith("http")
            ? calc.pinterest.image_url
            : `${BACKEND_BASE}${calc.pinterest.image_url}`)
        : null;
    const breadcrumbLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: FRONTEND_BASE || "/" },
            { "@type": "ListItem", position: 2, name: "Tools", item: `${FRONTEND_BASE}/tools` },
            { "@type": "ListItem", position: 3, name: calc?.title || slug, item: canonical },
        ],
    }), [calc, slug, canonical]);
    const calculatorLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: calc?.title || "",
        applicationCategory: "FinanceApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: 0, priceCurrency: "USD" },
        description: calc?.seo?.description || calc?.short_description || "",
        url: canonical,
    }), [calc, canonical]);

    useToolSeo({
        title: calc?.seo?.title || calc?.title,
        description: calc?.seo?.description || calc?.short_description,
        canonical,
        ogImage: ogImageUrl,
        structuredData: calc ? { "@graph": [breadcrumbLd, calculatorLd] } : null,
    });

    if (loading) {
        return (
            <PublicLayout>
                <div className="container mx-auto px-4 py-16 text-center text-bl-muted">
                    Loading calculator…
                </div>
            </PublicLayout>
        );
    }
    if (!calc) return null;

    return (
        <PublicLayout>

            <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
                {/* Breadcrumbs */}
                <nav className="text-xs text-bl-muted mb-6 flex items-center gap-1.5">
                    <Link to="/tools" className="hover:text-bl flex items-center gap-1">
                        <ChevronLeft className="h-3 w-3" />
                        All tools
                    </Link>
                </nav>

                {/* Header */}
                <header className="space-y-3 mb-8">
                    {calc.pinterest?.image_url && (
                        <div
                            className="rounded-xl overflow-hidden border border-bl/20 mb-4 max-w-2xl"
                            data-testid="calc-hero-cover"
                        >
                            <img
                                src={calc.pinterest.image_url}
                                alt={calc.pinterest.title || calc.title}
                                className="w-full h-auto block"
                                loading="eager"
                            />
                        </div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        {categoryLabel(calc.category) || "Tool"}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-bl leading-tight">
                        {calc.title}
                    </h1>
                    <p className="text-bl-muted text-base sm:text-lg leading-relaxed max-w-2xl">
                        {calc.short_description}
                    </p>
                </header>

                {/* Form + result two-column on desktop */}
                <div className="grid lg:grid-cols-5 gap-8 items-start">
                    <div className="lg:col-span-2">
                        <div className="rounded-xl border border-bl/30 bg-bl-surface p-5 sticky top-24">
                            <CalculatorForm
                                schema={calc.input_schema}
                                busy={busy}
                                onSubmit={handleSubmit}
                                onReset={() => {
                                    setResult(null);
                                    setSubmittedInputs(null);
                                }}
                            />
                        </div>
                    </div>

                    <div id="calc-result" className="lg:col-span-3 space-y-8">
                        {gated && (
                            <SoftPaywallGate
                                toolSlug={calc.slug || slug}
                                onUnlock={unlock}
                            />
                        )}
                        {!result && !gated && (
                            <div className="rounded-xl border border-dashed border-bl/30 p-8 text-center text-sm text-bl-muted">
                                Enter your inputs and we&apos;ll show you the estimated
                                exposure, severity, urgency, and recommended next steps.
                            </div>
                        )}
                        {result && (
                            <CalculatorResult
                                calc={calc}
                                inputs={submittedInputs}
                                result={result}
                            />
                        )}

                        {/* Long description block */}
                        <section className="space-y-3 pt-6 border-t border-bl/20">
                            <h2 className="text-lg font-semibold text-bl">
                                About this calculator
                            </h2>
                            <p className="text-sm text-bl-muted leading-relaxed">
                                {calc.long_description}
                            </p>
                        </section>

                        {/* Calculator → Full Report bridge */}
                        <section className="rounded-xl border border-bl/30 bg-bl-bg p-5 space-y-2">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                                Free calculator vs full Buyer&apos;s Leverage report
                            </div>
                            <h3 className="text-base font-semibold text-bl">
                                What this calculator shows you
                            </h3>
                            <ul className="text-sm text-bl space-y-1 list-disc pl-5">
                                <li>Estimated repair exposure range</li>
                                <li>Severity, urgency, and negotiation relevance for this issue</li>
                                <li>General next-step checklist</li>
                            </ul>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted pt-3">
                                What a full Buyer&apos;s Leverage report unlocks
                            </div>
                            <ul className="text-sm text-bl space-y-1 list-disc pl-5">
                                <li>Issue-by-issue inspection analysis across your whole report</li>
                                <li>Total repair exposure with prioritization</li>
                                <li>Negotiation strategy + seller-credit guidance</li>
                                <li>Repair timeline and specialist recommendations</li>
                                <li>Related-issue patterns the inspector may have missed</li>
                            </ul>
                            <Link
                                to="/upload"
                                className="bl-btn-primary text-sm mt-2"
                                data-testid="calc-bridge-cta"
                            >
                                Upload inspection report
                            </Link>
                        </section>
                    </div>
                </div>

                {/* Related calculators */}
                {relatedCalcs.length > 0 && (
                    <section className="mt-16 space-y-4">
                        <h2 className="text-xl font-bold text-bl flex items-center gap-2">
                            <Wrench className="h-4 w-4 text-bl-teal" />
                            Related calculators
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {relatedCalcs.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/tools/${c.slug}`}
                                    className="rounded-lg border border-bl/30 bg-bl-surface p-4 hover:border-bl-teal transition-colors"
                                    data-testid={`related-calc-${c.slug}`}
                                >
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                                        {categoryLabel(c.category)}
                                    </div>
                                    <div className="font-semibold text-bl mt-1">{c.title}</div>
                                    <p className="text-xs text-bl-muted mt-1.5 line-clamp-2">
                                        {c.short_description}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Related articles (placeholder section — pulls from calc config) */}
                {calc.related_article_slugs?.length > 0 && (
                    <section className="mt-12 space-y-3">
                        <h2 className="text-xl font-bold text-bl flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-bl-teal" />
                            Related reading
                        </h2>
                        <div className="space-y-2">
                            {calc.related_article_slugs.map((s) => (
                                <Link
                                    key={s}
                                    to={`/learn/${s}`}
                                    className="block text-sm text-bl-teal hover:underline"
                                >
                                    {s.replace(/-/g, " ")}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* FAQ */}
                {calc.faq?.length > 0 && (
                    <section className="mt-16">
                        <CalculatorFAQ faq={calc.faq} />
                    </section>
                )}

                {/* Disclaimer */}
                <footer className="mt-16 pt-6 border-t border-bl/20 text-[11px] text-bl-muted leading-relaxed">
                    Calculator results are estimates for educational planning only.
                    Actual repair costs, negotiation outcomes, and professional
                    recommendations vary by property, location, contractor,
                    inspection findings, and market conditions. Buyer&apos;s Leverage
                    does not replace licensed inspectors, contractors, engineers,
                    real estate agents, attorneys, lenders, or insurance professionals.
                </footer>
            </div>
        </PublicLayout>
    );
}
